<template>
  <div class="viewer-wrapper">
    <div ref="container" class="viewer-container"></div>
    <div class="canvas-debug">
      <canvas v-for="(t, i) in textures" :key="i" :ref="'canvas' + i" :width=t.texture.width :height=t.texture.height style="display: none;"></canvas>
    </div>
  </div>
</template>

<script setup>
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { onMounted, ref, onBeforeUnmount, toRefs, nextTick } from 'vue'

const props = defineProps({
  glbPath: { type: String, required: true },
  textures: { type: Array, default: () => [] }
})

const { glbPath, textures } = toRefs(props)
const container = ref(null)

let scene, camera, renderer, controls, animationId

function canvasTextureFromBase64(texData, bgColor = null) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    // Allow cross-origin images (S3 signed URLs) to be drawn to canvas
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      let canvasWidth = texData.width
      let canvasHeight = texData.height
      const angle = texData.rotate || 0
      const rad = (angle * Math.PI) / 180

      // rotate on 90º or 270º
      if (angle % 180 !== 0) {
        [canvasWidth, canvasHeight] = [canvasHeight, canvasWidth]
      }

      const canvas = document.createElement('canvas')
      canvas.width = canvasWidth
      canvas.height = canvasHeight
      const ctx = canvas.getContext('2d')

      if (bgColor) {
        ctx.fillStyle = bgColor
        ctx.fillRect(0, 0, canvasWidth, canvasHeight)
      }

      if (angle) {
        // calculations for rotation
        ctx.translate(canvasWidth / 2, canvasHeight / 2)
        ctx.rotate(rad)
        ctx.translate(-texData.width / 2, -texData.height / 2)
      }

      ctx.drawImage(
        img,
        texData.posx || 0,
        texData.posy || 0,
        texData.width,
        texData.height
      )

      const texture = new THREE.CanvasTexture(canvas)
      texture.wrapS = THREE.ClampToEdgeWrapping
      texture.wrapT = THREE.ClampToEdgeWrapping
      texture.repeat.set(1, 1)
      texture.flipY = false

      resolve({ canvas, texture })
    }
    img.onerror = () => reject(new Error(`Failed to load texture: ${texData.dataUrl?.substring(0, 80)}`))
    img.src = texData.dataUrl
  })
}


async function init() {
  scene = new THREE.Scene()
  camera = new THREE.PerspectiveCamera(45, container.value.clientWidth/container.value.clientHeight, 0.001, 1)
  camera.position.set(0.05,0.03,0.08)
  camera.lookAt(0,0,0)

  renderer = new THREE.WebGLRenderer({ antialias:true, alpha:true })
  renderer.toneMapping = THREE.NoToneMapping
  renderer.setSize(container.value.clientWidth, container.value.clientHeight)
  container.value.appendChild(renderer.domElement)

  controls = new OrbitControls(camera, renderer.domElement)
  controls.enableDamping = true
  controls.dampingFactor = 0.05
  controls.enableZoom = true
  controls.target.set(0,0,0)

  // Light — uniform flat lighting so the 3D preview matches the editor colours
  const ambient = new THREE.AmbientLight(0xffffff, 1.0)
  scene.add(ambient)

  // DEBUG
  await nextTick()
  const debugCanvases = textures.value.map((_, i) => container.value.nextSibling.querySelectorAll('canvas')[i])

  // load GLB
  const loader = new GLTFLoader()
  loader.load(glbPath.value, async (gltf) => {
    const cube = gltf.scene
    cube.rotation.x = Math.PI;   // flip 180º on X
    cube.rotation.y = Math.PI;   // flip 180º on Y

    const textureMap = {}

    for (let i = 0; i < textures.value.length; i++) {
      const t = textures.value[i]
      if (t.texture?.dataUrl) {
        try {
          const bgColor = t.gblVariable === 'Right' ? '#ffffff' : '#000000'
          const { canvas, texture } = await canvasTextureFromBase64(t.texture, bgColor)
          textureMap[t.gblVariable] = texture

          // DEBUG
          if (debugCanvases[i]) {
            const ctx = debugCanvases[i].getContext('2d')
            ctx.clearRect(0, 0, debugCanvases[i].width, debugCanvases[i].height)
            ctx.drawImage(canvas, 0, 0, debugCanvases[i].width, debugCanvases[i].height)
          }
        } catch (e) {
          console.warn('Texture load failed:', e.message)
        }
      }
    }

    cube.traverse((node) => {
      if (node.isMesh) {
        const materials = Array.isArray(node.material) ? node.material : [node.material]
        const newMaterials = materials.map((mat) => {
          const tex = textureMap[mat.name]
          if (tex) {
            return new THREE.MeshBasicMaterial({
              map: tex,
              side: THREE.DoubleSide,
            })
          }
          return new THREE.MeshBasicMaterial({
            color: mat.name === 'Right' ? 0xffffff : 0x000000,
            side: THREE.DoubleSide,
          })
        })
        node.material = newMaterials.length === 1 ? newMaterials[0] : newMaterials
      }
    })

    scene.add(cube)
  })
}

function animate() {
  animationId = requestAnimationFrame(animate)
  controls.update()
  renderer.render(scene, camera)
}

onMounted(() => {
  init()
  animate()
})

onBeforeUnmount(() => {
  cancelAnimationFrame(animationId)
  renderer.dispose()
})
</script>

<style>
.viewer-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #f0f0f0;
}

.viewer-container {
  width: 800px;
  height: 600px;
  position: relative;
  border: 1px solid #ccc;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
}
.canvas-debug canvas {
  border: 1px solid #999;
}
</style>
